import { useState, useCallback } from 'react'
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, Chip, Alert, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material'
import { Add, Delete, ContentCopy } from '@mui/icons-material'
import { useApi, apiPost, apiDelete } from '../../api/client'
import { ConfirmDialog } from '../../components/shared/ConfirmDialog'
import { useToastStore } from '../../stores/toast.store'
import { formatDatetimeInTimezone } from '../../utils/timezones'
import { useAuthStore } from '../../stores/auth.store'
import type { ApiResponse, ObjectPermission } from '@crm/shared'

interface TokenRow {
  id: string
  name: string
  token_prefix: string
  permissions: string | null
  created_by: string
  last_used_at: string | null
  expires_at: string | null
  created_at: string
}

interface ObjectItem {
  id: string
  name: string
  label: string
}

interface UserItem {
  id: string
  display_name: string
  username: string
}

function parsePermissions(permissions: string | null): Record<string, string> {
  if (!permissions) return {}
  try {
    const parsed = JSON.parse(permissions)
    return parsed?.object_permissions ?? {}
  } catch {
    return {}
  }
}

const PERM_LABELS: Record<string, string> = {
  read: 'Read',
  read_write: 'Read/Write',
  full: 'Full',
}

export function ApiTokenManagement() {
  const timezone = useAuthStore((s) => s.user?.timezone) ?? 'UTC'
  const { data, mutate } = useApi<ApiResponse<TokenRow[]>>('/settings/api-tokens')
  const { data: objectsData } = useApi<ApiResponse<ObjectItem[]>>('/objects')
  const { data: usersData } = useApi<ApiResponse<UserItem[]>>('/users')
  const toast = useToastStore()

  const tokens = data?.data ?? []
  const objects = objectsData?.data ?? []
  const users = usersData?.data ?? []
  const [dialogOpen, setDialogOpen] = useState(false)
  const [form, setForm] = useState({ name: '' })
  const [selectedObjects, setSelectedObjects] = useState<string[]>([])
  const [objectPerms, setObjectPerms] = useState<Record<string, ObjectPermission>>({})
  const [saving, setSaving] = useState(false)
  const [createdToken, setCreatedToken] = useState<string | null>(null)
  const [tokenObjectsOpen, setTokenObjectsOpen] = useState<Record<string, boolean>>({})
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  const handleCreate = useCallback(async () => {
    setSaving(true)
    try {
      const permissions: { object_permissions?: Record<string, ObjectPermission> } = {}
      if (selectedObjects.length > 0) {
        permissions.object_permissions = {}
        for (const objId of selectedObjects) {
          permissions.object_permissions[objId] = objectPerms[objId] ?? 'read'
        }
      }
      const result = await apiPost<{ data: { token: string; id: string } }, { name: string; permissions: typeof permissions }>('/settings/api-tokens', {
        name: form.name,
        permissions,
      })
      setCreatedToken(result.data.token)
      mutate()
      toast.success('Token 已创建')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '创建失败')
    } finally {
      setSaving(false)
    }
  }, [form, selectedObjects, objectPerms, mutate])

  const handleDelete = useCallback(async (id: string) => {
    try {
      await apiDelete(`/settings/api-tokens/${id}`)
      mutate()
      toast.success('Token 已撤销')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '删除失败')
    }
    setDeleteConfirm(null)
  }, [mutate, toast])

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setCreatedToken(null)
    setForm({ name: '' })
    setSelectedObjects([])
    setObjectPerms({})
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">API Tokens</Typography>
        <Button variant="contained" size="small" startIcon={<Add />} onClick={() => setDialogOpen(true)}>New Token</Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Prefix</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Created By</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Objects</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Last Used</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 60 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {tokens.map((token) => {
              const creator = users.find((u) => u.id === token.created_by)
              const objPerms = parsePermissions(token.permissions)
              return (
                <TableRow key={token.id}>
                  <TableCell>{token.name}</TableCell>
                  <TableCell><Chip label={token.token_prefix + '...'} size="small" variant="outlined" /></TableCell>
                  <TableCell>{creator?.display_name ?? token.created_by}</TableCell>
                  <TableCell>
                    {Object.keys(objPerms).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">All</Typography>
                    ) : (
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          multiple
                          value={Object.keys(objPerms)}
                          open={tokenObjectsOpen[token.id] ?? false}
                          onOpen={() => setTokenObjectsOpen((p) => ({ ...p, [token.id]: true }))}
                          onClose={() => setTokenObjectsOpen((p) => ({ ...p, [token.id]: false }))}
                          renderValue={() => <Typography variant="body2">{Object.keys(objPerms).length} objects</Typography>}
                          sx={{ '& .MuiSelect-icon': { display: 'none' } }}
                        >
                          {Object.entries(objPerms).map(([objId, perm]) => {
                            const obj = objects.find((o) => o.id === objId)
                            return <MenuItem key={objId} value={objId} sx={{ pointerEvents: 'none' }}>{obj?.label ?? objId} ({PERM_LABELS[perm] ?? perm})</MenuItem>
                          })}
                        </Select>
                      </FormControl>
                    )}
                  </TableCell>
                  <TableCell>{token.last_used_at ? formatDatetimeInTimezone(token.last_used_at, timezone) : 'Never'}</TableCell>
                  <TableCell>{formatDatetimeInTimezone(token.created_at, timezone)}</TableCell>
                  <TableCell>
                    <IconButton size="small" color="error" onClick={() => setDeleteConfirm(token.id)}><Delete fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{createdToken ? 'Token Created' : 'New API Token'}</DialogTitle>
        <DialogContent>
          {createdToken ? (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>Copy this token now. You won't be able to see it again!</Alert>
              <TextField value={createdToken} fullWidth size="small" multiline rows={3} slotProps={{ input: { readOnly: true } }} />
              <Button size="small" startIcon={<ContentCopy />} onClick={() => navigator.clipboard.writeText(createdToken)} sx={{ mt: 1 }}>
                Copy to Clipboard
              </Button>
            </Box>
          ) : (
            <>
              <TextField label="Token Name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} fullWidth size="small" sx={{ mt: 1 }} />
              <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                <InputLabel>Object Permissions</InputLabel>
                <Select
                  multiple
                  value={selectedObjects}
                  onChange={(e) => {
                    const val = e.target.value as string[]
                    setSelectedObjects(val)
                    setObjectPerms((prev) => {
                      const next = { ...prev }
                      for (const id of val) {
                        if (!next[id]) next[id] = 'read'
                      }
                      return next
                    })
                  }}
                  label="Object Permissions"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {(selected as string[]).map((id) => {
                        const obj = objects.find((o) => o.id === id)
                        return <Chip key={id} label={obj?.label ?? id} size="small" />
                      })}
                    </Box>
                  )}
                >
                  {objects.map((obj) => (
                    <MenuItem key={obj.id} value={obj.id}>{obj.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedObjects.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">Permission Levels</Typography>
                  {selectedObjects.map((objId) => {
                    const obj = objects.find((o) => o.id === objId)
                    return (
                      <Box key={objId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ flex: 1 }}>{obj?.label ?? objId}</Typography>
                        <Select
                          size="small"
                          value={objectPerms[objId] ?? 'read'}
                          onChange={(e) => setObjectPerms((prev) => ({ ...prev, [objId]: e.target.value as ObjectPermission }))}
                          sx={{ minWidth: 130 }}
                        >
                          <MenuItem value="read">Read</MenuItem>
                          <MenuItem value="read_write">Read / Write</MenuItem>
                          <MenuItem value="full">Full</MenuItem>
                        </Select>
                      </Box>
                    )
                  })}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{createdToken ? 'Close' : 'Cancel'}</Button>
          {!createdToken && (
            <Button onClick={handleCreate} variant="contained" disabled={saving}>{saving ? <CircularProgress size={20} /> : 'Create'}</Button>
          )}
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteConfirm !== null}
        title="Revoke Token"
        message="Are you sure you want to revoke this token? Any integrations using it will lose access."
        confirmLabel="Revoke"
        confirmColor="error"
        onConfirm={() => handleDelete(deleteConfirm!)}
        onCancel={() => setDeleteConfirm(null)}
      />
    </Box>
  )
}
